import React, { useState } from 'react';
import { Palette, Sun, Moon, Check } from 'lucide-react';
import { useTheme, themes, ThemeName } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';

export const ThemeSelector: React.FC = () => {
  const { themeName, themeMode, setTheme, toggleThemeMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleThemeChange = async (newTheme: ThemeName) => {
    try {
      await setTheme(newTheme);
      toast.success(`Tema alterado para ${themes[newTheme].displayName}`);
      setIsOpen(false);
    } catch (error) {
      toast.error('Erro ao alterar tema');
    }
  };

  const handleModeToggle = async () => {
    try {
      await toggleThemeMode();
      const newMode = themeMode === 'light' ? 'escuro' : 'claro';
      toast.success(`Modo ${newMode} ativado`);
    } catch (error) {
      toast.error('Erro ao alterar modo');
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        title="Personalizar tema"
      >
        <Palette className="w-5 h-5 text-gray-600 dark:text-gray-300" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
              <div className="flex items-center space-x-2">
                <Palette className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h3 className="font-semibold text-gray-700 dark:text-white">Personalizar Tema</h3>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Escolha cores e modo de exibição
              </p>
            </div>

            <div className="p-4">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Modo de Exibição</span>
                  <button
                    onClick={handleModeToggle}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {themeMode === 'light' ? (
                      <>
                        <Sun className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Claro</span>
                      </>
                    ) : (
                      <>
                        <Moon className="w-4 h-4 text-indigo-400" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Escuro</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-3">
                  Esquema de Cores
                </span>
                <div className="grid grid-cols-2 gap-2">
                  {Object.values(themes).map((theme) => (
                    <button
                      key={theme.name}
                      onClick={() => handleThemeChange(theme.name)}
                      className={`group relative p-3 rounded-lg border-2 transition-all ${
                        themeName === theme.name
                          ? 'border-current bg-gray-50 dark:bg-gray-700'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                      style={{
                        borderColor: themeName === theme.name ? theme.colors.primary : undefined
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div
                          className="w-6 h-6 rounded-full shadow-sm"
                          style={{ background: theme.colors.gradient }}
                        />
                        {themeName === theme.name && (
                          <Check className="w-4 h-4" style={{ color: theme.colors.primary }} />
                        )}
                      </div>
                      <div className="text-left">
                        <span className="text-xs font-medium text-gray-700 dark:text-white block">
                          {theme.displayName}
                        </span>
                        <div className="flex items-center space-x-1 mt-1">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: theme.colors.primary }}
                          />
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: theme.colors.accent }}
                          />
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: theme.colors.success }}
                          />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Suas preferências são salvas automaticamente
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
